import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  StatCard,
  ContentCard,
  DashboardHeader,
  Icons,
} from "../common/DashboardComponents";
import Loader from "../common/Loader";
import { fetchGrievances, fetchGrievanceSections, fetchGrievancesBySection } from "../../redux/actions/grievanceActions1";
import "bootstrap-icons/font/bootstrap-icons.css";

// Import grievance list configuration
import { 
  getStatusOptions,
  getFilteredGrievances,
  getGrievanceStats,
  getGrievanceListTitle,
  getTableColumns
} from "../common/GrievanceListConfig";

// Pagination logic helper for ellipsis-style pagination
function getPagination(current, total) {
  const delta = 2; // how many pages to show around current
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  for (let i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push("...");
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
}

const GrievanceList = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { grievances,grievanceSections,  loading: grievancesLoading } = useSelector(
    (state) => state.grievances
  );
// console.log(grievancesLoading ,'grievancesAA')

  // User role - default to Branch (3)
  const [userRole, setUserRole] = useState(3);
  
  const [filteredGrievances, setFilteredGrievances] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [isCompleted, setIsCompleted] = useState(false);
  const [isPendingAt, setIsPendingAt] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [grievancesPerPage, setGrievancesPerPage] = useState(10);

  // Check for isCompleted parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isCompletedParam = urlParams.get('isCompleted');
    const isPendingAtParam = urlParams.get('isPendingAt');
    
    if (isCompletedParam === 'true') {
      setIsCompleted(true);
      setStatusFilter('Completed');
    }
    
    if (isPendingAtParam) {
      setIsPendingAt(decodeURIComponent(isPendingAtParam));
    }
  }, [location.search]);

  // Load user role from localStorage (UI only — does not trigger API)
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData && userData.role) {
        setUserRole(userData.role);
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
  }, []);

  // Fetch grievance list once per page visit (single Grievance API call)
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData) return;

      if (userData.role === 9) {
        dispatch(fetchGrievancesBySection(userData.UNIT_CENTRE_ID));
      } else {
        dispatch(fetchGrievances());
      }
    } catch (error) {
      console.error("Error loading grievances:", error);
    }
  }, [dispatch, location.key]);

  // Load section filter options only when user opens the dropdown (avoids 2nd Grievance call on mount)
  const ensureSectionsLoaded = () => {
    if (!grievanceSections || grievanceSections.length === 0) {
      dispatch(fetchGrievanceSections());
    }
  };

  useEffect(() => {
    // Filter grievances based on role and search/status filters
    if (grievances.length === 0) return;

    // Get role-based filtered grievances
    // let result = getFilteredGrievances(userRole, grievances);

    let result = [...grievances];

    if (userRole !== 9) {
      result = getFilteredGrievances(userRole, grievances);
    }

    // Then filter by selected status
    if (statusFilter !== "all") {
      result = result.filter((g) => g.STATUS_NAME === statusFilter);
    }

    // Then filter by selected section
    if (sectionFilter !== "all") {
      result = result.filter((g) => g.SECTION_ID === sectionFilter);
    }

    // Then filter by pending role
    if (isPendingAt) {
      result = result.filter((g) => g.PENDING_ROLE_NAME === isPendingAt);
    }

    // Then filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (g) =>
          (g.GRV_ID && g.GRV_ID.toString().toLowerCase().includes(term)) ||
          (g.USER_NAME && g.USER_NAME.toLowerCase().includes(term)) ||
          (g.GRV_CAT && g.GRV_CAT.toLowerCase().includes(term)) ||
          (g.UNIT_CENTRE_NAME &&
            g.UNIT_CENTRE_NAME.toLowerCase().includes(term))
      );
    }

    setFilteredGrievances(result);
    setCurrentPage(1); // Reset to first page whenever filters change
  }, [grievances, searchTerm, statusFilter, userRole, isCompleted, sectionFilter, isPendingAt]);

  const handleSectionFilterChange = (e) => {
    setSectionFilter(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  // Get grievance stats based on role
  const grievanceStats = getGrievanceStats(userRole, grievances);

  // Get table columns based on role
  const tableColumns = getTableColumns(userRole, navigate);

  // Get status options for filter dropdown based on role
  const statusOptions = getStatusOptions(userRole, grievances);
  
  // Get grievance list title based on role
  const grievanceListTitle = getGrievanceListTitle(userRole);

  // Get current grievances for pagination
  const indexOfLastGrievance = currentPage * grievancesPerPage;
  const indexOfFirstGrievance = indexOfLastGrievance - grievancesPerPage;
  const currentGrievances = filteredGrievances.slice(
    indexOfFirstGrievance,
    indexOfLastGrievance
  );

  const totalPages = Math.ceil(filteredGrievances.length / grievancesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Go to previous page
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Change items per page
  const handleItemsPerPageChange = (e) => {
    setGrievancesPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };



  return (
    <div className="container-fluid">
      <DashboardHeader title="Grievance Management" />

      {/* Stats Cards based on role */}
      <div className="stats-grid">
        {grievanceStats.map((stat, index) => (
          <StatCard
            key={index}
            // title={stat.title}
            title={'Total Grievance'}
            value={stat.value}
            icon={stat.icon}
            color={stat.color}
            additionalInfo={
              stat.additionalInfo ? (
                <span>{stat.additionalInfo}</span>
              ) : null
            }
          />
        ))}
      </div>

      {/* Filters */}
      <ContentCard title="Filters" icon={Icons.Filter}>
        <div className="row g-3">
          <div className="col-md-3">
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by ID, applicant, category..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>

         {userRole !== 9 && (
            <div className="col-md-3">
              <select
                className="form-select"
                value={sectionFilter}
                onChange={handleSectionFilterChange}
                onFocus={ensureSectionsLoaded}
                onMouseDown={ensureSectionsLoaded}
              >
                <option value="all">All Sections</option>
                {grievanceSections.map((section) => (
                  <option key={section.SECTION_ID} value={section.SECTION_ID}>
                    {section.SECTION_NAME}
                  </option>
                ))}
              </select>
            </div>
          )} 

          <div className="col-md-3">
            <select
              className="form-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              {statusOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <select
              className="form-select"
              value={grievancesPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value={5}>5 items per page</option>
              <option value={10}>10 items per page</option>
              <option value={20}>20 items per page</option>
              <option value={50}>50 items per page</option>
            </select>
          </div>
        </div>
      </ContentCard>

      {/* Grievance List */}
      <ContentCard title={grievanceListTitle} icon={Icons.Grievances}>
        {grievancesLoading ? (
          <Loader message="Loading grievances..." />
        ) : (
          <>
            <div className="table-responsive">
              <table className="table border-top border-bottom">
                <thead className="bg-light">
                  <tr>
                    {tableColumns.map((column, index) => (
                      <th className="py-3" key={index}>
                        <div className="d-flex align-items-center">
                          <span>{column.header}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentGrievances.length === 0 ? (
                    <tr>
                      <td colSpan={tableColumns.length} className="text-center py-4">
                        No grievances found
                      </td>
                    </tr>
                  ) : (
                    currentGrievances.map((grievance) => (
                      <tr key={grievance?.GRV_ID} className="border-bottom">
                        {tableColumns.map((column, colIndex) => (
                          <td className="py-2" key={colIndex}>
                            {column.render(grievance)}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredGrievances.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                  Showing {indexOfFirstGrievance + 1} to {Math.min(indexOfLastGrievance, filteredGrievances.length)} of {filteredGrievances.length} entries
                </div>
                <nav aria-label="Grievance pagination">
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={prevPage}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {getPagination(currentPage, totalPages).map((page, idx) =>
                      page === "..." ? (
                        <li key={idx} className="page-item disabled">
                          <span className="page-link">…</span>
                        </li>
                      ) : (
                        <li
                          key={idx}
                          className={`page-item ${currentPage === page ? "active" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => paginate(page)}
                            style={currentPage === page ? { border: "1px solid #000" } : {}}
                          >
                            {page}
                          </button>
                        </li>
                      )
                    )}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}
      </ContentCard>
    </div>
  );
};

export default GrievanceList;